<template>
  <v-container fluid grid-list-md>
    <AppTitleBar>
      <template slot="title">{{ $t(`labels.${title}`) }}</template>
      <template slot="actions">
        <v-tooltip top transition="fade-transition">
          <template v-slot:activator="{ on }">
            <v-btn icon small @click="create" v-on="on" v-if="canCreate">
              <v-icon small v-text="$vuetify.icons.values.add" />
            </v-btn>
          </template>
          <span>{{ $t('new') }}</span>
        </v-tooltip>
        <v-menu bottom left min-width="150" v-if="$board.isAllowed('create')">
          <template #activator="{ on: menu }">
            <v-tooltip top transition="fade-transition">
              <template #activator="{ on: tooltip }">
                <v-btn icon small slot="activator">
                  <v-icon small v-text="$vuetify.icons.values.options" v-on="{ ...tooltip, ...menu }" />
                </v-btn>
              </template>
              <span>{{ $t('labels.actions') }}</span>
            </v-tooltip>
          </template>
          <v-list>
            <v-list-item v-if="$board.isAllowed('create')" @click="showRestore">
              <v-list-item-title>{{ $t('restore') }}</v-list-item-title>
            </v-list-item>
          </v-list>
        </v-menu>
      </template>
    </AppTitleBar>
    <v-row no-gutters justify="end">
      <v-col cols="12" sm="6" md="4" lg="3">
        <SingleSearchFilter :context="context" :fields="searchFields" />
      </v-col>
    </v-row>
    <v-loading v-if="loading" />
    <v-data-iterator
      v-else
      :items="boards"
      :server-items-length="boardsCount"
      :page.sync="page"
      :sort-by.sync="sortBy"
      :sort-desc.sync="sortDesc"
      :items-per-page.sync="itemsPerPage"
      :footer-props="{ itemsPerPageOptions: itemsPerPageArray }"
      row
      wrap
    >
      <template v-slot:default="{ items }">
        <v-row>
          <v-col v-for="item in items" :key="item.id" cols="12" sm="6" md="4" lg="3">
            <slot name="card" :board="item" :show="goToBoard">
              <board-card :board="item" :show="goToBoard" />
            </slot>
          </v-col>
        </v-row>
      </template>
    </v-data-iterator>
    <router-view></router-view>
  </v-container>
</template>

<script>
import { mapState, mapActions } from 'vuex'
import { AppTitleBar, SingleSearchFilter, VLoading } from '@argon/app/components'
import { Datatable } from '@argon/app/mixins'
import { ConfirmDeleteBoardDialog, BoardCard } from '../components'
import { NAMESPACE as REALTIME_NAMESPACE } from '@argon/realtime/store'
import { NAMESPACE, ALLBOARDS } from '../store'
import boardPerimeter from '../perimeters'
import { create } from 'vue-modal-dialogs'

const confirmDeleteAction = create(ConfirmDeleteBoardDialog)

export default {
  name: 'BoardListWidget',
  components: {
    AppTitleBar,
    VLoading,
    SingleSearchFilter,
    BoardCard
  },
  perimeters: [boardPerimeter],
  mixins: [Datatable],
  props: {
    orgRef: String,
    entityType: { type: String, default: 'allBoards' },
    title: { type: String, default: 'boards' },
    searchFields: { type: Array, default: () => ['display'] },
    readOnly: { type: Boolean, default: false },
    boardLink: { type: String, default: 'board' },
    newBoardLink: { type: String, default: 'newBoard' },
    restoreLink: { type: String, default: 'boardRestore' }
  },
  data: () => ({
    loading: true,
    context: NAMESPACE
  }),
  computed: {
    ...mapState(NAMESPACE, ['boards', 'selectableTypes', 'boardsCount']),
    canCreate() {
      return !this.readOnly && this.$board.isAllowed('create')
    },
    realtimeKey() {
      return this.$store.getters[`${REALTIME_NAMESPACE}/realtimeKey`]({
        category: 'boardsList',
        identifier: this.entityType
      })
    }
  },
  methods: {
    ...mapActions(REALTIME_NAMESPACE, ['addListener', 'removeListener']),
    ...mapActions(NAMESPACE, ['listBoards', 'clearBoards', 'setBoardTypeFilter']),
    goToBoard(ref) {
      this.$router.push({
        name: this.boardLink,
        params: { ...this.$router.params, boardRef: ref }
      })
    },
    showRestore() {
      this.$router.push({ name: this.restoreLink })
    },
    create() {
      this.$router.push({ name: this.newBoardLink })
    },
    destroyBoard(item) {
      confirmDeleteAction().then((res) => {
        if (res) {
          this.deleteBoard(item).then(() => {
            this.fetchData()
          })
        }
      })
    },
    fetchData() {
      this.loading = true
      let entityType = this.entityType === ALLBOARDS ? null : this.entityType
      return this.setBoardTypeFilter(entityType)
        .then(() => {
          return this.listBoards({ pager: this.pager })
        })
        .finally(() => {
          this.loading = false
        })
    }
  },
  watch: {
    realtimeKey: function (v, oldV) {
      if (oldV) {
        this.fetchData()
      }
    }
  },
  created() {
    this.fetchData().then(() => {
      this.addListener({
        category: 'boardsList',
        identifier: this.entityType
      })
    })
  },
  destroyed() {
    this.removeListener({ category: 'boardsList', identifier: this.entityType })
    this.clearBoards()
  }
}
</script>

<i18n>
{
  "en": {
    "new": "New Board",
    "restore": "@:labels.restore",
    "title": "@:labels.boards"
  },
  "fr": {
    "new": "Nouveau tableau",
    "restore": "@:labels.restore",
    "title": "@:labels.boards"
  }
}
</i18n>
